import logger from "../utils/logger";
import io from "socket.io-client";
import { getBackendUrl } from "../config";
import api from "./api";

class SocketWorker {
  constructor(companyId, userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.eventListeners = {};
      this.isRefreshingToken = false;
      this.lastRefreshAttemptAt = 0;
      this.configureSocket();
      SocketWorker.instance = this;
    } else {
      SocketWorker.instance.updateContext(companyId, userId);
    }

    return SocketWorker.instance;
  }

  static getStoredToken() {
    const rawToken = localStorage.getItem("token");
    if (!rawToken) return null;

    try {
      const parsedToken = JSON.parse(rawToken);
      if (typeof parsedToken === "string" && parsedToken.trim() !== "") {
        return parsedToken;
      }
    } catch {
      // token salvo como string simples
    }

    const normalized = rawToken.replace(/^"|"$/g, "").trim();
    return normalized || null;
  }

  static getSocketUrl(companyId) {
    const backendUrl = (getBackendUrl() || process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendUrl) return "";
    if (!companyId) return backendUrl;
    return `${backendUrl}/${companyId}`;
  }

  updateContext(companyId, userId) {
    const nextCompanyId = companyId || "";
    const nextUserId = userId || "";

    const hasChanged = this.companyId !== nextCompanyId || this.userId !== nextUserId;
    this.companyId = nextCompanyId;
    this.userId = nextUserId;

    if (hasChanged) {
      this.recreateSocket();
      return;
    }

    this.refreshHandshakeAuth();
  }

  refreshHandshakeAuth() {
    if (!this.socket) return;

    const token = SocketWorker.getStoredToken();
    const authPayload = { token, userId: this.userId };
    this.socket.auth = authPayload;

    if (this.socket.io?.opts) {
      this.socket.io.opts.auth = authPayload;
      this.socket.io.opts.query = {
        ...(this.socket.io.opts.query || {}),
        token,
        userId: this.userId,
      };
    }
  }

  async refreshAccessToken() {
    if (this.isRefreshingToken) return false;
    const now = Date.now();
    if (now - this.lastRefreshAttemptAt < 30000) return false;

    this.lastRefreshAttemptAt = now;
    this.isRefreshingToken = true;

    try {
      const { data } = await api.post("/auth/refresh_token");
      if (!data?.token) return false;
      localStorage.setItem("token", JSON.stringify(data.token));
      this.refreshHandshakeAuth();
      return true;
    } catch (error) {
      logger.warn("Falha ao atualizar token para reconexao do socket");
      return false;
    } finally {
      this.isRefreshingToken = false;
    }
  }

  rebindListeners() {
    if (!this.socket) return;

    Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
      callbacks.forEach((callback) => this.socket.on(event, callback));
    });
  }

  recreateSocket() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.configureSocket();
    this.rebindListeners();
  }

  configureSocket() {
    const socketUrl = SocketWorker.getSocketUrl(this.companyId);

    if (!socketUrl) {
      logger.warn("Socket URL nao configurada (REACT_APP_BACKEND_URL ausente)");
      return;
    }

    if (!this.companyId) {
      logger.warn("Socket nao iniciado: companyId ausente");
      return;
    }

    this.socket = io(socketUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      transports: ["polling", "websocket"],
      tryAllTransports: true,
      upgrade: true,
      auth: {
        token: SocketWorker.getStoredToken(),
        userId: this.userId,
      },
      query: {
        token: SocketWorker.getStoredToken(),
        userId: this.userId,
      },
    });

    this.socket.io.on("reconnect_attempt", () => {
      this.refreshHandshakeAuth();
    });

    this.socket.on("connect", () => {
      logger.info("Conectado ao servidor Socket.IO");
    });

    this.socket.on("connect_error", async (error) => {
      const message = error?.message || "erro desconhecido";
      logger.warn(`Falha na conexao Socket.IO: ${message}`);
      const normalizedMessage = String(message)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      const unauthorized =
        normalizedMessage.includes("err_session_expired") ||
        normalizedMessage.includes("unauthorized") ||
        (normalizedMessage.includes("token") &&
          (normalizedMessage.includes("invalid") ||
            normalizedMessage.includes("ausente") ||
            normalizedMessage.includes("missing")));
      const shouldTryRefresh = unauthorized || normalizedMessage.includes("websocket error");

      if (shouldTryRefresh) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          this.connect();
          return;
        }
      }

      this.refreshHandshakeAuth();
    });

    this.socket.on("disconnect", (reason) => {
      logger.info("Desconectado do servidor Socket.IO");
      if (reason !== "io client disconnect") {
        this.reconnectAfterDelay();
      }
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }

    if (!this.eventListeners[event].includes(callback)) {
      this.eventListeners[event].push(callback);
    }

    this.connect();
    this.socket.on(event, callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    if (!this.socket || !this.eventListeners[event]) return;

    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter((cb) => cb !== callback);
      return;
    }

    this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
    delete this.eventListeners[event];
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      logger.info("Socket desconectado manualmente");
    }

    this.eventListeners = {};
    SocketWorker.instance = null;
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        logger.info("Tentando reconectar apos desconexao");
        this.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
    if (!this.socket) return;

    this.refreshHandshakeAuth();

    if (!this.socket.connected) {
      try {
        this.socket.connect();
      } catch {
        // noop
      }
    }
  }

  forceReconnect() {
    this.recreateSocket();
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
